import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    FileText, ArrowLeft, Upload, Clock, CheckCircle2, Calendar,
    User, MessageSquare, RotateCcw, Send, BookOpen, Paperclip, History, ShieldCheck, Target, ListChecks
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { formatAisModuleLabel, hasAisModuleMetadata } from '../utils/aisModules';
import { formatDueDateTime, isPastDue } from '../utils/assignmentDates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
    submitted: { bg: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Submitted' },
    graded: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Graded' },
    returned: { bg: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Returned' },
};

export default function AssignmentDetail() {
    const { assignmentId } = useParams();
    const { user } = useAuth();
    const [assignment, setAssignment] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [mySubmission, setMySubmission] = useState(null);
    const [loading, setLoading] = useState(true);

    const [showSubmitDialog, setShowSubmitDialog] = useState(false);
    const [submitContent, setSubmitContent] = useState('');
    const [submitFile, setSubmitFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [activity, setActivity] = useState([]);
    const [evidenceItems, setEvidenceItems] = useState([]);

    // Instructor: inline grading state (per submission id)
    const [editingGrade, setEditingGrade] = useState({});
    const [statusFilter, setStatusFilter] = useState('all');

    // Return dialog state
    const [showReturnDialog, setShowReturnDialog] = useState(false);
    const [returnTarget, setReturnTarget] = useState(null);
    const [returnFeedback, setReturnFeedback] = useState('');
    const [returning, setReturning] = useState(false);

    const isInstructor = user.role === 'instructor' || user.role === 'admin';

    const fetchAssignmentData = useCallback(async () => {
        try {
            const assignmentsRes = await axios.get(`${API}/assignments`);
            const found = assignmentsRes.data.find(a => a.id === assignmentId);
            setAssignment(found);
            if (found) {
                try {
                    const activityRes = await axios.get(`${API}/evidence/activity?assignment_id=${assignmentId}`);
                    setActivity(activityRes.data.activity || []);
                    setEvidenceItems(activityRes.data.evidence_items || []);
                } catch {
                    setActivity([]);
                    setEvidenceItems([]);
                }
            }
            if (isInstructor) {
                const subRes = await axios.get(`${API}/assignments/${assignmentId}/submissions`);
                setSubmissions(subRes.data);
            } else {
                const myRes = await axios.get(`${API}/submissions/my`);
                setMySubmission(myRes.data.find(s => s.assignment_id === assignmentId) || null);
            }
        } catch { toast.error('Failed to load assignment'); }
        finally { setLoading(false); }
    }, [assignmentId, isInstructor]);

    useEffect(() => { fetchAssignmentData(); }, [fetchAssignmentData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const wasReturned = mySubmission?.status === 'returned';
            const response = await axios.post(`${API}/submissions`, {
                assignment_id: assignmentId, content: submitContent
            });
            let savedSubmission = response.data;
            if (submitFile) {
                try {
                    const formData = new FormData();
                    formData.append('file', submitFile);
                    const uploadRes = await axios.post(`${API}/submissions/${response.data.id}/upload`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    savedSubmission = {
                        ...savedSubmission,
                        file_url: uploadRes.data.file_url,
                        file_name: uploadRes.data.file_name,
                        evidence_items: uploadRes.data.evidence_item ? [uploadRes.data.evidence_item] : savedSubmission.evidence_items,
                    };
                } catch {
                    toast.error('Submission saved, but the evidence file did not upload.');
                }
            }
            setMySubmission(savedSubmission);
            setShowSubmitDialog(false);
            setSubmitContent('');
            setSubmitFile(null);
            toast.success(wasReturned ? 'Re-submitted!' : 'Assignment submitted!');
            fetchAssignmentData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit'); }
        finally { setSubmitting(false); }
    };

    const handleGradeInline = async (sub) => {
        const g = editingGrade[sub.id];
        if (!g || g.grade === undefined || g.grade === '') { toast.error('Enter a grade'); return; }
        try {
            const rubricScores = getRubricCriteria(assignment).length ? normalizeRubricScoresForSave(g.rubric_scores) : [];
            const gradeValue = rubricScores.length ? sumRubricScore(rubricScores) : parseFloat(g.grade);
            await axios.put(`${API}/submissions/grade`, {
                submission_id: sub.id,
                grade: gradeValue,
                feedback: g.feedback || null,
                rubric_scores: rubricScores,
                outcome_feedback: g.outcome_feedback || null,
            });
            toast.success(`Graded ${sub.student_name}`);
            setEditingGrade(prev => { const n = { ...prev }; delete n[sub.id]; return n; });
            fetchAssignmentData();
        } catch { toast.error('Failed to save grade'); }
    };

    const handleReturn = async () => {
        if (!returnTarget) return;
        setReturning(true);
        try {
            await axios.put(`${API}/submissions/return`, { submission_id: returnTarget.id, feedback: returnFeedback || null });
            toast.success(`Returned to ${returnTarget.student_name}`);
            setShowReturnDialog(false);
            setReturnTarget(null);
            setReturnFeedback('');
            fetchAssignmentData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to return'); }
        finally { setReturning(false); }
    };

    const openReturnDialog = (sub) => {
        setReturnTarget(sub);
        setReturnFeedback(sub.feedback || '');
        setShowReturnDialog(true);
    };

    const startEditing = (sub) => {
        const rubricScores = buildInitialRubricScores(assignment, sub);
        const grade = rubricScores.length ? sumRubricScore(rubricScores).toString() : (sub.grade?.toString() || '');
        setEditingGrade(prev => ({
            ...prev,
            [sub.id]: {
                grade,
                feedback: sub.feedback || '',
                outcome_feedback: sub.outcome_feedback || '',
                rubric_scores: rubricScores,
            }
        }));
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#2F8FCB] border-t-transparent rounded-full animate-spin" /></div>;
    if (!assignment) return <div className="text-center py-12"><p className="text-slate-500">Assignment not found</p><Link to="/assignments"><Button variant="outline" className="mt-4">Back</Button></Link></div>;

    const isOverdue = isPastDue(assignment.due_date);
    const canSubmit = !isInstructor && (!mySubmission || mySubmission.status === 'returned');

    const filteredSubs = statusFilter === 'all'
        ? submissions
        : submissions.filter(s => s.status === statusFilter);
    const assignmentEvidence = evidenceItems.length
        ? evidenceItems
        : submissions.flatMap(s => getSubmissionEvidence(s)).concat(mySubmission ? getSubmissionEvidence(mySubmission) : []);
    const rubricCriteria = getRubricCriteria(assignment);
    const hasRubric = rubricCriteria.length > 0;
    const backToAssignments = assignment?.course_id
        ? `/courses/${assignment.course_id}?tab=assignments`
        : '/assignments';

    return (
        <div className="space-y-6" data-testid="assignment-detail-page">
            <Link to={backToAssignments} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-4 h-4" /> Back to Assignments
            </Link>

            {/* Assignment Header */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isOverdue ? 'bg-red-50' : 'bg-[#E0F2FE]'}`}>
                        <FileText className={`w-7 h-7 ${isOverdue ? 'text-red-500' : 'text-[#2F8FCB]'}`} />
                    </div>
                    <div className="flex-1">
                        <span className="text-sm text-slate-500">{assignment.course_title}</span>
                        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 mt-1">{assignment.title}</h1>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                            <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : 'text-slate-500'}`}>
                                <Calendar className="w-4 h-4" />
                                Due: {formatDueDateTime(assignment.due_date)}
                            </span>
                            <span className="text-slate-500">Max Score: <strong>{assignment.max_score}</strong></span>
                        </div>
                    </div>
                    {canSubmit && (
                        <Button onClick={() => { setSubmitContent(mySubmission?.content || ''); setSubmitFile(null); setShowSubmitDialog(true); }}
                            className="bg-[#2F8FCB] hover:bg-[#2577a8] rounded-full" data-testid="submit-assignment-btn">
                            <Upload className="w-4 h-4 mr-2" /> {mySubmission?.status === 'returned' ? 'Re-submit' : 'Submit'}
                        </Button>
                    )}
                </div>
            </div>

            {hasAisModuleMetadata(assignment) && (
                <div className="bg-sky-50 border border-sky-100 rounded-2xl p-5" data-testid="ais-module-workflow-panel">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center border border-sky-100">
                            <BookOpen className="w-5 h-5 text-[#2F8FCB]" />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs uppercase tracking-wide text-sky-700 font-semibold">AIS Module Workflow</p>
                            <h2 className="text-lg font-semibold text-slate-900 mt-1">{formatAisModuleLabel(assignment)}</h2>
                            {assignment.ais_cycle && <p className="text-sm text-slate-600 mt-1">{assignment.ais_cycle}</p>}
                            {assignment.required_workspace_tabs?.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Workspace areas to use</p>
                                    <div className="flex flex-wrap gap-2">
                                        {assignment.required_workspace_tabs.map((tab) => (
                                            <span key={tab} className="rounded-full bg-white border border-sky-100 px-3 py-1 text-xs font-medium text-slate-700">
                                                {tab}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {assignment.expected_deliverables?.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Expected deliverables</p>
                                    <ul className="grid gap-1.5 text-sm text-slate-700">
                                        {assignment.expected_deliverables.map((item) => (
                                            <li key={item} className="flex items-start gap-2">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="mt-4">
                                <Link to={`/workspace?course_id=${encodeURIComponent(assignment.course_id)}`}>
                                    <Button variant="outline" className="rounded-full bg-white" data-testid="open-workspace-from-assignment">
                                        Open Accounting Workspace
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <LearningAssessmentPanel assignment={assignment} />

            {/* Instructions Section */}
            {assignment.description && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="assignment-instructions">
                    <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#2F8FCB]" /> Instructions
                    </h2>
                    <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{assignment.description}</div>
                </div>
            )}

            {/* ── Student: My Submission ── */}
            {!isInstructor && mySubmission && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Your Submission
                        </h2>
                        <StatusBadge status={mySubmission.status} />
                    </div>
                    <div className="space-y-4">
                        <div className="p-4 bg-slate-50 rounded-xl">
                            <p className="text-slate-700">{mySubmission.content || 'No content provided'}</p>
                            <div className="flex items-center gap-3 mt-2">
                                <p className="text-xs text-slate-400">Submitted: {new Date(mySubmission.submitted_at).toLocaleString()}</p>
                                {mySubmission.is_late && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] rounded font-medium">LATE</span>}
                            </div>
                            <SubmissionEvidenceList submission={mySubmission} />
                        </div>

                        {mySubmission.status === 'returned' && (
                            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                                <p className="text-sm font-medium text-amber-700 flex items-center gap-1"><RotateCcw className="w-4 h-4" /> Returned for revision</p>
                                {mySubmission.feedback && <p className="text-amber-700 mt-1 text-sm">{mySubmission.feedback}</p>}
                                <p className="text-xs text-amber-500 mt-2">Click "Re-submit" above to submit a revised answer.</p>
                            </div>
                        )}

                        {mySubmission.status === 'graded' && mySubmission.grade != null && (
                            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-emerald-700 font-medium">Grade</span>
                                    <span className="text-2xl font-bold text-emerald-700">{mySubmission.grade}/{assignment.max_score}</span>
                                </div>
                                {mySubmission.feedback && (
                                    <div className="mt-3 pt-3 border-t border-emerald-200">
                                        <span className="text-sm text-emerald-600 font-medium flex items-center gap-1"><MessageSquare className="w-4 h-4" /> Feedback</span>
                                        <p className="text-emerald-700 mt-1">{mySubmission.feedback}</p>
                                    </div>
                                )}
                                <RubricFeedbackPanel submission={mySubmission} assignment={assignment} />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Instructor: Submissions List with inline grading ── */}
            {isInstructor && (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                        <div>
                            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Review Queue</p>
                            <h2 className="font-semibold font-['Manrope']">Student Submissions ({submissions.length})</h2>
                        </div>
                        <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg">
                            {[{ id: 'all', label: 'All' }, { id: 'submitted', label: 'Pending' }, { id: 'graded', label: 'Graded' }, { id: 'returned', label: 'Returned' }].map(f => (
                                <button key={f.id} onClick={() => setStatusFilter(f.id)}
                                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${statusFilter === f.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                                    data-testid={`filter-${f.id}`}>
                                    {f.label}
                                    {f.id !== 'all' && <span className="ml-1 text-[10px] opacity-60">({submissions.filter(s => s.status === f.id).length})</span>}
                                </button>
                            ))}
                        </div>
                    </div>

                    {filteredSubs.length === 0 ? (
                        <div className="p-12 text-center"><Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No submissions{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}</p></div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {filteredSubs.map((sub) => {
                                const isEditing = !!editingGrade[sub.id];
                                const g = editingGrade[sub.id] || {};
                                const rubricScoresForEdit = g.rubric_scores || [];
                                const rubricTotal = sumRubricScore(rubricScoresForEdit);
                                return (
                                    <div key={sub.id} className="p-4 hover:bg-slate-50/50" data-testid={`submission-${sub.id}`}>
                                        {/* Row 1: Student info + status + actions */}
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                    <User className="w-4 h-4 text-slate-400" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-medium text-slate-900 text-sm">{sub.student_name}</h3>
                                                        <StatusBadge status={sub.status} />
                                                        {sub.is_late && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] rounded font-medium">LATE</span>}
                                                    </div>
                                                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{sub.content || 'No content'}</p>
                                                    <p className="text-xs text-slate-400 mt-1">Submitted: {new Date(sub.submitted_at).toLocaleString()}</p>
                                                    <SubmissionEvidenceList submission={sub} compact />
                                                    {!isEditing && <RubricFeedbackPanel submission={sub} assignment={assignment} compact />}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                {sub.status === 'graded' && sub.grade != null && !isEditing && (
                                                    <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">{sub.grade}/{assignment.max_score}</span>
                                                )}
                                                {!isEditing && (
                                                    <Button size="sm" variant="outline" onClick={() => startEditing(sub)} className="text-xs" data-testid={`grade-btn-${sub.id}`}>
                                                        {sub.status === 'graded' ? 'Edit Grade' : 'Grade'}
                                                    </Button>
                                                )}
                                                {sub.status !== 'returned' && (
                                                    <Button size="sm" variant="ghost" onClick={() => openReturnDialog(sub)} className="text-amber-600 text-xs" data-testid={`return-btn-${sub.id}`}>
                                                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Return
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Row 2: Inline grading (when editing) */}
                                        {isEditing && (
                                            <div className="mt-3 ml-12 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                                {hasRubric ? (
                                                    <div className="space-y-3" data-testid={`rubric-grading-${sub.id}`}>
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-sm font-semibold text-slate-800">Rubric scoring</p>
                                                                <p className="text-xs text-slate-500">Score each criterion. Total grade updates automatically.</p>
                                                            </div>
                                                            <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                                                                {rubricTotal}/{assignment.max_score}
                                                            </span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {rubricScoresForEdit.map((item, index) => (
                                                                <div key={item.criterion_id || index} className="grid gap-2 rounded-lg bg-white p-3 border border-slate-100 md:grid-cols-[1fr_96px_1fr] md:items-start">
                                                                    <div>
                                                                        <p className="text-sm font-medium text-slate-800">{item.title}</p>
                                                                        <p className="text-xs text-slate-500">Max {item.points} pts</p>
                                                                    </div>
                                                                    <div>
                                                                        <Label className="text-xs">Score</Label>
                                                                        <Input
                                                                            type="number"
                                                                            min="0"
                                                                            max={item.points}
                                                                            step="0.5"
                                                                            value={item.score}
                                                                            onChange={(e) => setEditingGrade(prev => updateRubricScore(prev, sub.id, index, 'score', e.target.value))}
                                                                            className="mt-1 h-8 text-sm"
                                                                            data-testid={`rubric-score-${sub.id}-${index}`}
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <Label className="text-xs">Criterion feedback</Label>
                                                                        <Input
                                                                            value={item.feedback || ''}
                                                                            onChange={(e) => setEditingGrade(prev => updateRubricScore(prev, sub.id, index, 'feedback', e.target.value))}
                                                                            placeholder="Optional note"
                                                                            className="mt-1 h-8 text-sm"
                                                                            data-testid={`rubric-feedback-${sub.id}-${index}`}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">Outcome feedback</Label>
                                                            <Input
                                                                value={g.outcome_feedback || ''}
                                                                onChange={(e) => setEditingGrade(prev => ({ ...prev, [sub.id]: { ...prev[sub.id], outcome_feedback: e.target.value } }))}
                                                                placeholder="What should the student improve next?"
                                                                className="mt-1 h-8 text-sm"
                                                                data-testid={`outcome-feedback-${sub.id}`}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">General feedback</Label>
                                                            <Input value={g.feedback} onChange={(e) => setEditingGrade(prev => ({ ...prev, [sub.id]: { ...prev[sub.id], feedback: e.target.value } }))}
                                                                placeholder="Optional overall feedback..." className="mt-1 h-8 text-sm" data-testid={`inline-feedback-${sub.id}`} />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-[120px_1fr] gap-3 items-end">
                                                        <div>
                                                            <Label className="text-xs">Grade (/{assignment.max_score})</Label>
                                                            <Input type="number" min="0" max={assignment.max_score} step="0.5"
                                                                value={g.grade} onChange={(e) => setEditingGrade(prev => ({ ...prev, [sub.id]: { ...prev[sub.id], grade: e.target.value } }))}
                                                                className="mt-1 h-8 text-sm" data-testid={`inline-grade-${sub.id}`} />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">Feedback</Label>
                                                            <Input value={g.feedback} onChange={(e) => setEditingGrade(prev => ({ ...prev, [sub.id]: { ...prev[sub.id], feedback: e.target.value } }))}
                                                                placeholder="Optional feedback..." className="mt-1 h-8 text-sm" data-testid={`inline-feedback-${sub.id}`} />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="mt-3 flex justify-end gap-1">
                                                    <Button size="sm" onClick={() => handleGradeInline(sub)} className="bg-emerald-600 h-8 text-xs" data-testid={`save-grade-${sub.id}`}>
                                                        <Send className="w-3.5 h-3.5 mr-1" /> Save
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditingGrade(prev => { const n = { ...prev }; delete n[sub.id]; return n; })} className="h-8 text-xs">
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <EvidenceActivityPanel activity={activity} evidenceItems={assignmentEvidence} isInstructor={isInstructor} />

            {/* Submit / Re-submit Dialog */}
            <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
                <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Manrope']">
                            {mySubmission?.status === 'returned' ? 'Re-submit Assignment' : 'Submit Assignment'}
                        </DialogTitle>
                        <DialogDescription>
                            {mySubmission?.status === 'returned'
                                ? 'Your previous submission was returned. Revise your answer below.'
                                : 'Enter your answer and submit.'}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                        <div>
                            <Label>Your Answer</Label>
                            <Textarea value={submitContent} onChange={(e) => setSubmitContent(e.target.value)}
                                placeholder="Enter your answer or explanation..." className="mt-1" rows={6} required data-testid="submit-content-input" />
                        </div>
                        <div>
                            <Label>Evidence file (optional)</Label>
                            <Input
                                type="file"
                                onChange={(e) => setSubmitFile(e.target.files?.[0] || null)}
                                className="mt-1"
                                data-testid="submit-evidence-file-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">Attach a source document, worksheet, or supporting file for instructor review.</p>
                        </div>
                        <DialogFooter className="sticky bottom-0 bg-white pt-3 border-t border-slate-100">
                            <Button type="button" variant="outline" onClick={() => { setShowSubmitDialog(false); setSubmitFile(null); }} className="rounded-full">Cancel</Button>
                            <Button type="submit" disabled={submitting} className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full" data-testid="confirm-submit-btn">
                                {submitting ? 'Submitting...' : mySubmission?.status === 'returned' ? 'Re-submit' : 'Submit'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Return to Student Dialog */}
            <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
                <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-amber-700 font-['Manrope']">Return to Student</DialogTitle>
                        <DialogDescription>
                            This submission will be sent back to <strong>{returnTarget?.student_name}</strong> for revision.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2">
                        <Label>Feedback (recommended)</Label>
                        <Textarea
                            value={returnFeedback}
                            onChange={(e) => setReturnFeedback(e.target.value)}
                            placeholder="Explain what needs to be revised..."
                            className="mt-1"
                            rows={4}
                            data-testid="return-feedback-input"
                        />
                    </div>
                    <DialogFooter className="sticky bottom-0 bg-white pt-3 border-t border-slate-100 mt-4">
                        <Button type="button" variant="outline" onClick={() => setShowReturnDialog(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={handleReturn} disabled={returning} className="bg-amber-600 hover:bg-amber-700 rounded-full" data-testid="confirm-return-btn">
                            {returning ? 'Returning...' : 'Return Submission'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatusBadge({ status }) {
    const style = STATUS_STYLES[status] || STATUS_STYLES.submitted;
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${style.bg}`} data-testid={`status-badge-${status}`}>{style.label}</span>;
}

function getRubricCriteria(assignment) {
    return assignment?.rubric_criteria?.filter(item => item?.title && Number(item.points) > 0) || [];
}

function getLearningOutcomes(assignment) {
    return assignment?.learning_outcomes?.filter(Boolean) || [];
}

function buildInitialRubricScores(assignment, submission) {
    const existing = submission?.rubric_scores || [];
    const existingById = existing.reduce((acc, item) => {
        acc[item.criterion_id] = item;
        return acc;
    }, {});

    return getRubricCriteria(assignment).map((criterion) => {
        const saved = existingById[criterion.id] || {};
        return {
            criterion_id: criterion.id,
            title: criterion.title,
            points: Number(criterion.points) || 0,
            score: saved.score ?? '',
            feedback: saved.feedback || '',
        };
    });
}

function normalizeRubricScoresForSave(items) {
    return (items || []).map((item) => ({
        criterion_id: item.criterion_id,
        title: item.title,
        points: Number(item.points) || 0,
        score: Number(item.score) || 0,
        feedback: item.feedback || null,
    }));
}

function sumRubricScore(items) {
    return Number((items || []).reduce((total, item) => total + (Number(item.score) || 0), 0).toFixed(2));
}

function updateRubricScore(prev, submissionId, index, field, value) {
    const current = prev[submissionId] || {};
    const nextScores = [...(current.rubric_scores || [])];
    nextScores[index] = { ...nextScores[index], [field]: value };
    const nextGrade = sumRubricScore(nextScores).toString();
    return {
        ...prev,
        [submissionId]: {
            ...current,
            grade: nextGrade,
            rubric_scores: nextScores,
        },
    };
}

function LearningAssessmentPanel({ assignment }) {
    const outcomes = getLearningOutcomes(assignment);
    const rubric = getRubricCriteria(assignment);
    if (!outcomes.length && !rubric.length) return null;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="assignment-learning-assessment-panel">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                    <Target className="w-5 h-5 text-violet-600" />
                </div>
                <div className="flex-1">
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Learning & Rubric</p>
                    <h2 className="text-lg font-semibold text-slate-900 mt-1">What this assignment measures</h2>
                    <p className="text-sm text-slate-500 mt-1">Use this as your guide before submitting or reviewing the work.</p>

                    {outcomes.length > 0 && (
                        <div className="mt-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Learning outcomes</p>
                            <ul className="grid gap-2 text-sm text-slate-700">
                                {outcomes.map((outcome, index) => (
                                    <li key={`${outcome}-${index}`} className="flex gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span>{outcome}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {rubric.length > 0 && (
                        <div className="mt-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Rubric criteria</p>
                            <div className="grid gap-2 md:grid-cols-2">
                                {rubric.map((criterion) => (
                                    <div key={criterion.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                                        <div className="flex items-start justify-between gap-3">
                                            <p className="text-sm font-medium text-slate-800">{criterion.title}</p>
                                            <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600">{criterion.points} pts</span>
                                        </div>
                                        {criterion.description && <p className="text-xs text-slate-500 mt-1">{criterion.description}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function RubricFeedbackPanel({ submission, assignment, compact = false }) {
    const scores = submission?.rubric_scores || [];
    const hasOutcomeFeedback = Boolean(submission?.outcome_feedback);
    if (!scores.length && !hasOutcomeFeedback) return null;

    return (
        <div className={`${compact ? 'mt-3' : 'mt-4 pt-3 border-t border-emerald-200'} rounded-xl ${compact ? 'bg-slate-50 border border-slate-100 p-3' : ''}`} data-testid={`rubric-feedback-${submission.id}`}>
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-violet-600" /> Rubric & outcomes
            </p>
            {scores.length > 0 && (
                <div className="mt-2 grid gap-2">
                    {scores.map((item, index) => (
                        <div key={item.criterion_id || index} className="flex items-start justify-between gap-3 text-sm">
                            <div>
                                <p className="font-medium text-slate-700">{item.title}</p>
                                {item.feedback && <p className="text-xs text-slate-500">{item.feedback}</p>}
                            </div>
                            <span className="font-mono text-slate-700 flex-shrink-0">{item.score}/{item.points}</span>
                        </div>
                    ))}
                </div>
            )}
            {hasOutcomeFeedback && (
                <div className="mt-3 rounded-lg bg-white/70 px-3 py-2 border border-slate-100">
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Next learning focus</p>
                    <p className="text-sm text-slate-700 mt-1">{submission.outcome_feedback}</p>
                </div>
            )}
            {!scores.length && getRubricCriteria(assignment).length > 0 && (
                <p className="mt-2 text-xs text-slate-500">Rubric criteria exist for this assignment, but no criterion scores were saved yet.</p>
            )}
        </div>
    );
}

function getSubmissionEvidence(submission) {
    if (!submission) return [];
    if (submission.evidence_items?.length) return submission.evidence_items;
    if (submission.file_url && submission.file_name) {
        return [{
            id: `${submission.id}-legacy-file`,
            file_url: submission.file_url,
            file_name: submission.file_name,
            uploaded_at: submission.submitted_at,
            uploaded_by_name: submission.student_name,
        }];
    }
    return [];
}

function buildFileUrl(fileUrl) {
    if (!fileUrl) return '#';
    if (fileUrl.startsWith('http')) return fileUrl;
    return `${process.env.REACT_APP_BACKEND_URL}${fileUrl}`;
}

function formatAuditAction(action) {
    return (action || 'Activity').toLowerCase().split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function SubmissionEvidenceList({ submission, compact = false }) {
    const items = getSubmissionEvidence(submission);
    if (!items.length) return null;

    return (
        <div className={`${compact ? 'mt-2' : 'mt-3'} flex flex-wrap gap-2`} data-testid={`submission-evidence-${submission.id}`}>
            {items.map((item) => (
                <a
                    key={item.id || item.file_url}
                    href={buildFileUrl(item.file_url)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-sky-100 bg-white px-2.5 py-1 text-xs font-medium text-sky-700 hover:border-sky-200 hover:bg-sky-50"
                    data-testid={`submission-evidence-link-${submission.id}`}
                >
                    <Paperclip className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{item.file_name}</span>
                </a>
            ))}
        </div>
    );
}

function EvidenceActivityPanel({ activity, evidenceItems, isInstructor }) {
    const hasEvidence = evidenceItems?.length > 0;
    const hasActivity = activity?.length > 0;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="assignment-evidence-activity-panel">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Pilot Control</p>
                    <h2 className="text-lg font-semibold font-['Manrope'] text-slate-900 mt-1 flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-[#2F8FCB]" /> Evidence & Activity
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        {isInstructor
                            ? 'Review submitted files and the action trail for this assignment.'
                            : 'Your submitted evidence and assignment activity are tracked here.'}
                    </p>
                </div>
                <div className="flex gap-2 text-xs">
                    <span className="rounded-full bg-sky-50 px-2.5 py-1 font-medium text-sky-700">{evidenceItems.length} evidence</span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600">{activity.length} events</span>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2 mt-5">
                <div className="rounded-xl border border-slate-100 p-4">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-sky-600" /> Evidence Files
                    </h3>
                    {hasEvidence ? (
                        <div className="mt-3 space-y-2">
                            {evidenceItems.slice(0, 8).map((item) => (
                                <a
                                    key={item.id || item.file_url}
                                    href={buildFileUrl(item.file_url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm hover:bg-sky-50"
                                    data-testid="assignment-evidence-file-link"
                                >
                                    <span className="truncate font-medium text-slate-700">{item.file_name}</span>
                                    <span className="text-xs text-slate-400 flex-shrink-0">{item.uploaded_by_name || 'Student'}</span>
                                </a>
                            ))}
                        </div>
                    ) : (
                        <p className="mt-3 text-sm text-slate-500">No evidence files attached yet.</p>
                    )}
                </div>

                <div className="rounded-xl border border-slate-100 p-4">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <History className="w-4 h-4 text-slate-500" /> Activity Trail
                    </h3>
                    {hasActivity ? (
                        <div className="mt-3 space-y-3">
                            {activity.slice(0, 8).map((event) => (
                                <div key={event.id} className="border-l-2 border-slate-200 pl-3">
                                    <p className="text-sm font-medium text-slate-800">{formatAuditAction(event.action)}</p>
                                    <p className="text-xs text-slate-500">{event.details}</p>
                                    <p className="text-[11px] text-slate-400 mt-0.5">
                                        {event.user_name} - {new Date(event.timestamp).toLocaleString()}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="mt-3 text-sm text-slate-500">No assignment activity recorded yet.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
